import { getCategoriesForEmployer } from '@ben/columbia/api';
import Select from '@ben/orion/deprecated-select';
import type { ICategory } from '@ben/shared/types/columbia';
import { useQuery } from '@tanstack/react-query';
import type { ForwardedRef } from 'react';
import { forwardRef, memo, useCallback, useMemo, useRef } from 'react';
import AddCategoryModal from './add-category-modal';

interface ICategoriesSelect {
  employer: number | undefined;
  onChange: (selectedCategories: ICategory[] | undefined) => void;
  defaultValue: string[];
  hasError?: boolean;
  width?: string;
}

const CategoriesSelect = forwardRef(
  (
    { employer, onChange, hasError, width, defaultValue }: ICategoriesSelect,
    ref: ForwardedRef<unknown>
  ) => {
    const { isLoading, data } = useQuery(
      [`categories-for-employer-${employer}`],
      getCategoriesForEmployer(employer),
      { enabled: Boolean(employer) }
    );
    const addCategoryModalRef = useRef<{
      showModal: (categoryName: string) => void;
    }>(null);
    const options = useMemo(() => {
      return isLoading || !data
        ? []
        : data
            .sort(({ rank: rank1 }, { rank: rank2 }) => rank1 - rank2)
            .map((category) => ({
              key: String(category.id),
              label: String(category.name),
            }));
    }, [data, isLoading]);

    const handleShowModal = useCallback(
      (categoryName: string) => {
        addCategoryModalRef?.current?.showModal(categoryName);
      },
      [addCategoryModalRef]
    );

    const handleOnChange = useCallback(
      (values: string[]) => {
        const selectedCategories = data?.filter((category) =>
          values.includes(String(category.id))
        );
        onChange(selectedCategories);
      },
      [data, onChange]
    );

    return (
      <>
        <Select
          ref={ref}
          disabled={isLoading}
          options={options}
          name={'Category (optional)'}
          defaultValue={defaultValue}
          onChange={(values) => handleOnChange(values as string[])}
          hasError={hasError}
          width={width}
          showSearch
          onCreateOption={handleShowModal}
          canCreateOption
          searchPlaceholder="Search categories"
        />
        {typeof employer === 'number' && (
          <AddCategoryModal ref={addCategoryModalRef} employer={employer} />
        )}
      </>
    );
  }
);

CategoriesSelect.displayName = 'CategoriesSelect';

export default memo(CategoriesSelect);
